import type { ReactNode } from 'react';
import { DateIndex } from '@/components/date-index';
import { ArticleCard } from '@/components/article-card';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import type { ArticleSearch } from '@/lib/use-article-search';
import type { SearchResultItem } from '@shared/schemas';

type Props = ArticleSearch & {
  /** Passato a `<DateIndex>` per i link dell'indice (default home). */
  basePath?: string;
  /** Azioni admin per riga (modifica/elimina); assente nella vista pubblica. */
  renderActions?: (item: SearchResultItem) => ReactNode;
};

/**
 * Indice per data (quando non c'è ricerca attiva) oppure elenco risultati con
 * intestazione di ordinamento, contatore e paginazione. Condiviso fra la home e
 * la gestione articoli admin.
 */
export function ArticleResults({
  isActive,
  search,
  sort,
  setSort,
  backToIndex,
  basePath,
  renderActions,
}: Props) {
  if (!isActive) return <DateIndex basePath={basePath} />;

  // useInfiniteSearch: i risultati arrivano a pagine accumulate. Le appiattisco in
  // un'unica lista; il totale/“raffina” vengono dalla prima pagina.
  const pages = search.data?.pages ?? [];
  const items = pages.flatMap((p) => p.items);
  const first = pages[0];
  const total = first?.total ?? 0;

  return (
    <section>
      <div className="flex flex-wrap items-center justify-between gap-2 mb-2">
        <Button variant="ghost" size="sm" onClick={backToIndex} className="-ml-2">
          ← Torna all'indice
        </Button>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger className="h-8 w-auto gap-1.5 text-xs">
            <span className="text-(--color-muted-foreground)">Ordina:</span>
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="date_desc">Data, più recenti</SelectItem>
            <SelectItem value="date_asc">Data, meno recenti</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {search.isLoading && !search.data ? (
        <p className="py-12 text-center text-sm text-(--color-muted-foreground)">Ricerca…</p>
      ) : search.error ? (
        <p className="py-12 text-center text-sm text-(--color-destructive)">Errore: {search.error.message}</p>
      ) : items.length === 0 ? (
        <p className="py-12 text-center text-sm text-(--color-muted-foreground)">Nessun risultato.</p>
      ) : (
        <>
          <div className="flex items-baseline justify-between text-xs text-(--color-muted-foreground) pb-2 border-b border-(--color-border)">
            <p>
              {first?.capped ? (
                <>Mostro i primi {total.toLocaleString('it-IT')} risultati — raffina la ricerca per restringere</>
              ) : (
                <>
                  {total.toLocaleString('it-IT')} risultat{total === 1 ? 'o' : 'i'}
                </>
              )}
            </p>
            <p>Mostrati {items.length.toLocaleString('it-IT')}</p>
          </div>

          {items.map((item) => (
            <ArticleCard key={item.id} item={item} actions={renderActions?.(item)} />
          ))}

          {search.hasNextPage && (
            <div className="flex justify-center py-6">
              <Button
                variant="outline"
                onClick={() => search.fetchNextPage()}
                disabled={search.isFetchingNextPage}
              >
                {search.isFetchingNextPage ? 'Caricamento…' : 'Carica altri'}
              </Button>
            </div>
          )}
        </>
      )}
    </section>
  );
}
